import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';

// LESSON_SUITE_V1 SlotGrid — TimeCenter-style tappable week of open/busy slots.
// Props: trainerId, serviceId, onPick(slot {start,end,seats_left?}), days (default 7)

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const fmtSlotTime = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(' ', '\u00A0');
const fmtDayNum = (dateStr) => {
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
const addDaysStr = (dateStr, n) => {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + n);
  const pad = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const todayStr = () => {
  const d = new Date();
  const pad = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const SlotGrid = ({ trainerId, serviceId, onPick, days = 7 }) => {
  const [startDate, setStartDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!trainerId || !serviceId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get(`/booking/trainers/${trainerId}/slots`, { params: { service_id: serviceId, start_date: startDate, days } });
      setData(resp.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load open times');
    } finally {
      setLoading(false);
    }
  }, [trainerId, serviceId, startDate, days]);

  useEffect(() => { load(); }, [load]);

  if (!trainerId || !serviceId) return null;

  const isToday = startDate === todayStr();
  const dayCols = (data?.days || []).filter(d => d.slots.length > 0);
  const noAvailability = data && dayCols.length === 0;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <button
          className="btn btn-secondary btn-sm"
          disabled={isToday || loading}
          onClick={() => setStartDate(addDaysStr(startDate, -days))}
          style={{ opacity: isToday ? 0.4 : 1 }}
        >&#8249; Prev</button>
        <div style={{ fontWeight: 700, fontSize: 13 }}>
          {fmtDayNum(startDate)} – {fmtDayNum(addDaysStr(startDate, days - 1))}
        </div>
        <button className="btn btn-secondary btn-sm" disabled={loading} onClick={() => setStartDate(addDaysStr(startDate, days))}>Next &#8250;</button>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 24 }}><div className="spinner" /></div>}
      {error && <div className="card" style={{ padding: 12, background: '#FEE2E2', color: '#991B1B', fontSize: 13 }}>{error}</div>}

      {!loading && noAvailability && (
        <div className="card" style={{ padding: 14, fontSize: 13, color: 'var(--text-secondary)' }}>
          No open times this week. Try the next week, or ask the trainer to add availability.
        </div>
      )}

      {!loading && dayCols.length > 0 && (
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6, WebkitOverflowScrolling: 'touch' }}>
          {dayCols.map(day => (
            <div key={day.date} style={{ minWidth: 96, flex: '0 0 auto' }}>
              <div style={{
                textAlign: 'center', padding: '6px 4px', borderRadius: 8, marginBottom: 6,
                background: day.date === todayStr() ? 'var(--cr-primary)' : 'var(--bg-secondary)',
                color: day.date === todayStr() ? '#F5EBDD' : 'var(--text-primary)',
              }}>
                <div style={{ fontSize: 11, fontWeight: 700 }}>{DAY_SHORT[day.day_of_week]}</div>
                <div style={{ fontSize: 11 }}>{fmtDayNum(day.date)}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {day.slots.map(slot => slot.status === 'open' ? (
                  <button
                    key={slot.start}
                    onClick={() => onPick(slot)}
                    style={{
                      border: '1px solid var(--cr-green)', borderRadius: 9, padding: '9px 4px',
                      background: 'var(--cr-green-light)', color: 'var(--cr-green-dark)',
                      fontSize: 12.5, fontWeight: 700, cursor: 'pointer', lineHeight: 1.2,
                    }}
                  >
                    {fmtSlotTime(slot.start)}
                    {slot.seats_left != null && <div style={{ fontSize: 10, fontWeight: 600 }}>{slot.seats_left} spot{slot.seats_left === 1 ? '' : 's'} left</div>}
                  </button>
                ) : (
                  <div
                    key={slot.start}
                    style={{
                      borderRadius: 9, padding: '9px 4px', textAlign: 'center',
                      background: '#FDECEC', color: '#B3766E', fontSize: 11, fontWeight: 600, lineHeight: 1.2,
                    }}
                  >
                    {slot.label || 'Booked'}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SlotGrid;
